import { useState, useMemo, useEffect } from 'react';
import {
  FileText as FileIcon,
  CheckCircle,
  Clock as ClockIcon,
  XCircle,
  Edit as EditIcon,
  Eye as EyeIcon,
  Filter as FilterIcon,
  Search as SearchIcon,
  ArrowUpDown,
  RussianRuble,
  TrendingUp,
  Clock,
  Edit3,
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { API_URL } from '../config';
import { authFetch } from '../utils/authFetch';


export function MyDocuments() {
  const [documents, setDocuments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortField, setSortField] = useState('title');
  const [sortOrder, setSortOrder] = useState('desc');
  const [editingId, setEditingId] = useState(null);
  const [priceDraft, setPriceDraft] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  const categories = [
    { id: 'workCult', name: 'Культура работы' },
    { id: 'projManag', name: 'Управление проектами' },
    { id: 'operEff', name: 'Операционная эффективность' },
    { id: 'hr', name: 'HR' },
    { id: 'demandPlann', name: ' Планирование потребности' },
    { id: 'provisPlann', name: 'Планирование обеспечения' },
    { id: 'purch', name: 'Закупки' },
    { id: 'logic', name: 'Логистика' },
    { id: 'money', name: 'Финансы' },
    { id: 'nsi', name: 'Развитие НСИ' },
    { id: 'automat', name: 'Автоматизация' },
    { id: 'strat', name: 'Стратегия' }
  ];

    const getCategoryName = (catId) => {
      const entry = categories.find(c => c.id === catId);
      return entry ? entry.name : catId;
    };


    const fetchDocuments = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await authFetch(`${API_URL}/documents`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (!res.ok) {
          throw new Error(`Server responded ${res.status}`);
        }
        const data = await res.json();
        setDocuments(data);
      } catch (err) {
        console.error('Ошибка при получении документов:', err);
      }
    };

  useEffect(() => {
    fetchDocuments();
  }, []);

  const changeDocPrice = async (docId, newPrice) => {
    const token = localStorage.getItem('token');
    const res = await authFetch(`${API_URL}/documents/price/${docId}`, {
        method: 'PATCH',
        headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
        },
        body: JSON.stringify({ price: Number(newPrice) })
    });
    if (!res.ok) throw new Error(`Ошибка ${res.status}`);
    await fetchDocuments();
    };

  const filteredDocuments = useMemo(() => {
    return documents
      .filter(doc => (
        (doc.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.category.toLowerCase().includes(searchTerm.toLowerCase())
      ))
      .filter(doc => statusFilter === 'all' || doc.status === statusFilter)
      .filter(doc => categoryFilter === 'all' || doc.category === categoryFilter)
      .sort((a, b) => {
        let aVal = a[sortField];
        let bVal = b[sortField];
        return sortOrder === 'asc'
          ? (aVal > bVal ? 1 : -1)
          : (aVal < bVal ? 1 : -1);
      });
  }, [documents, searchTerm, statusFilter, categoryFilter, sortField, sortOrder]);

  const startEdit = (doc) => {
    setEditingId(doc.id);
    setPriceDraft(String(doc.price ?? 0));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setPriceDraft('');
  };

  const saveEdit = async (doc) => {
    // валидация
    let val = priceDraft.replace(',', '.');
    if (val === '') val = '0';
    const parsed = Number.parseFloat(val);
    if (!Number.isFinite(parsed) || parsed < 0) {
      alert('Введите корректную цену (неотрицательное число).');
      return;
    }
    // округляем до 2 знаков
    const normalized = Math.round(parsed * 100) / 100;

    try {
      // оптимистичное обновление
      setDocuments(prev =>
        prev.map(d => (d.id === doc.id ? { ...d, price: normalized } : d))
      );
      await changeDocPrice(doc.id, normalized);
    } catch (e) {
      console.error(e);
      // если ошибка — перезагрузим данные
      await fetchDocuments();
      alert('Не удалось сохранить цену.');
    } finally {
      cancelEdit();
    }
  };

  const onRowKeyDown = (e, doc) => {
    if (e.key === 'Enter') saveEdit(doc);
    if (e.key === 'Escape') cancelEdit();
  };

  const getStatusBadge = status => {
    switch (status) {
      case 'approved':
        return (
          <Badge className="bg-green-100 text-green-800 border-green-200">
            <CheckCircle className="h-3 w-3 mr-1" />Активен
          </Badge>
        );
      case 'pending':
        return (
          <Badge variant="outline" className="text-yellow-600 border-yellow-200">
            <ClockIcon className="h-3 w-3 mr-1" />Модерация
          </Badge>
        );
      case 'rejected':
        return (
          <Badge variant="outline" className="text-red-600 border-red-200">
            <XCircle className="h-3 w-3 mr-1" />Отклонён
          </Badge>
        );
      case 'draft':
        return (
          <Badge variant="outline" className="text-gray-600 border-gray-200">
            <EditIcon className="h-3 w-3 mr-1" />Черновик
          </Badge>
        );
      default:
        return null;
    }
  };


  const totalEarnings = documents.reduce((sum, doc) => sum + Number(doc.price * doc.sales_count || 0), 0);
  const totalSales    = documents.reduce((sum, doc) => sum + Number(doc.sales_count    || 0), 0);

  const activeCount   = documents.filter(doc => doc.status === 'approved').length;
  const moderationCount = documents.filter(doc => doc.status === 'pending').length;

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <h1 className="gradient-text text-3xl font-bold">Мои документы</h1>
        <p className="text-gray-600">
          Управляйте загруженными документами и отслеживайте статистику продаж
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-r from-green-50 to-green-100 p-4 rounded-lg border border-green-200">
          <div className="flex items-center gap-2">
            <RussianRuble className="h-5 w-5 text-green-600" />
            <span className="text-sm text-green-700">Общий доход</span>
          </div>
            <p className="text-2xl text-green-800 mt-2">
              {totalEarnings.toLocaleString('ru-RU')}₽
            </p>
          </div>
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-blue-600" />
            <span className="text-sm text-blue-700">Продажи</span>
          </div>
          <p className="text-2xl text-blue-800 mt-2">{totalSales}</p>
        </div>
        <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-4 rounded-lg border border-purple-200">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-purple-600" />
            <span className="text-sm text-purple-700">Активные</span>
          </div>
          <p className="text-2xl text-purple-800 mt-2">{activeCount}</p>
        </div>
        <div className="bg-gradient-to-r from-orange-50 to-orange-100 p-4 rounded-lg border border-orange-200">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-orange-600" />
            <span className="text-sm text-orange-700">На модерации</span>
          </div>
          <p className="text-2xl text-orange-800 mt-2">{moderationCount}</p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FilterIcon className="h-5 w-5" />Фильтры и поиск
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Поиск документов..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger><SelectValue placeholder="Статус" /></SelectTrigger>
              <SelectContent className="bg-white shadow-lg z-50">
                <SelectItem value="all">Все статусы</SelectItem>
                <SelectItem value="approved">Активен</SelectItem>
                <SelectItem value="pending">Модерация</SelectItem>
                <SelectItem value="rejected">Отклонён</SelectItem>
                <SelectItem value="draft">Черновик</SelectItem>
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger><SelectValue placeholder="Категория" /></SelectTrigger>
              <SelectContent className="bg-white shadow-lg z-50">
                <SelectItem value="all">Все категории</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Select value={sortField} onValueChange={setSortField}>
                <SelectTrigger>
                  <ArrowUpDown className="h-4 w-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="title">Название</SelectItem>
                  <SelectItem value="price">Цена</SelectItem>
                  <SelectItem value="sales">Продажи</SelectItem>
                  <SelectItem value="earnings">Доход</SelectItem>
                  <SelectItem value="views">Просмотры</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSortOrder(o => o === 'asc' ? 'desc' : 'asc')}
              >
                {sortOrder === 'asc' ? '↑' : '↓'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {filteredDocuments.length ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="p-4 text-left">Документ</th>
                    <th className="p-4 text-left">Категория</th>
                    <th className="p-4 text-left">Цена</th>
                    <th className="p-4 text-left">Статус</th>
                    <th className="p-4 text-left">Продажи</th>
                    <th className="p-4 text-left">Доход</th>
                    <th className="p-4 text-left">Редактировать</th>
                    {/* <th className="p-4 text-left">Просмотры</th> */}
                    {/* <th className="p-4 text-left">Действия</th> */}
                  </tr>
                </thead>
                <tbody>
                  {filteredDocuments.map(doc => (
                    <tr key={doc.id} className="border-b hover:bg-gray-50">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                        <FileIcon className="h-5 w-5 text-blue-600" />
                        <div>
                          <p className="font-medium text-sm max-w-xs truncate">{doc.title}</p>
                        </div>
                        </div>
                      </td>
                      <td className="p-4 text-sm text-gray-600">{getCategoryName(doc.category)}</td>
                      <td className="p-4 font-medium">
                        {editingId === doc.id & editingId !== null ? (
                          <div className="flex items-center gap-2" onKeyDown={(e)=>onRowKeyDown(e, doc)}>
                            <Input
                              value={priceDraft}
                              onChange={(e) => setPriceDraft(e.target.value)}
                              className="h-8 w-28"
                              inputMode="decimal"
                              autoFocus
                            />
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span>{(doc.price ?? 0).toLocaleString('ru-RU')}₽</span>
                          </div>
                        )}
                      </td>
                      <td className="p-4">{getStatusBadge(doc.status)}</td>
                      <td className="p-4">{doc.sales_count}</td>
                      <td className="p-4 font-medium text-green-700">
                        {(Number(doc.price * doc.sales_count) || 0)}₽
                      </td>
                      <td className="p-4 font-medium">
                        <Button
                          className='bg-white text-gray-600 border-gray-300'
                          onClick={() => startEdit(doc)}
                        >
                          <Edit3 />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <FileIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">Документы не найдены</p>
              <p className="text-gray-400 text-sm mt-2">
                Попробуйте изменить параметры поиска или фильтры
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Moderation notice */}
      {moderationCount > 0 && (
        <Card>
          <CardContent>
            <div className="flex items-start gap-3">
              <ClockIcon className="h-5 w-5 text-yellow-600 mt-0.5" />
              <div>
                <p className="font-medium text-yellow-800">Документы на модерации</p>
                <p className="text-sm text-yellow-700 mt-1">
                  У вас {moderationCount} документ(ов) на модерации. Обычно проверка занимает 1-2 рабочих дня.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
